import { Controller } from 'react-hook-form'
import React from 'react'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'

import { cn } from 'src/utils'
import { InputType } from 'src/types'
import { DATE_FORMAT } from 'src/constants'

import { ButtonNavigateToCars } from 'src/features/ButtonNavigateToCars'
import Container from 'src/components/Container'
import AppBreadcrumb from 'src/components/AppBreadcrumb'

import { useMaintenance } from 'src/pages/ServiceMaintenance/useMaintenance'
import { getFieldValidationRules } from 'src/pages/ServiceMaintenance/helpers'
import { Textarea } from 'src/components/shadcn/textarea'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'src/components/shadcn/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/shadcn/popover'
import { Input } from 'src/components/shadcn/input'
import { Card, CardContent, CardHeader } from 'src/components/shadcn/card'
import { Calendar } from 'src/components/shadcn/calendar'
import { Button } from 'src/components/shadcn/button'

const ServiceMaintenance = (): JSX.Element => {
  const {
    locale,
    fields,
    selectFields,
    onSubmit,
    errors,
    handleSubmit,
    setValue,
    control,
    isLoadingAddMaintenanceHistory,
    getFormattedTitle,
    breadcrumbItems,
  } = useMaintenance()

  return (
    <Container>
      <AppBreadcrumb items={breadcrumbItems} />
      <div className="flex justify-center w-full">
        <Card className="w-full md:w-2/4 mt-8 rounded-xl">
          <CardHeader className="p-6 border-b mb-2">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold">{locale.pageTitle}</h1>
              <ButtonNavigateToCars />
            </div>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <h1 className="text-lg font-medium mb-2">
                  {locale.serviceDate}
                </h1>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-60 justify-start text-left font-normal',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          <CalendarIcon />
                          {field.value ? (
                            format(new Date(field.value), DATE_FORMAT)
                          ) : (
                            <span>{locale.serviceDatePlaceholder}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto rounded-md">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={date =>
                            setValue('date', date ?? field.value)
                          }
                          initialFocus
                          toDate={new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                />
              </div>

              <div>
                {fields.map(({ name, label, inputType, placeholder }) =>
                  inputType === InputType.SELECT ? (
                    <div key={name}>
                      <h1 className="text-lg font-medium mb-2">
                        {locale.serviceType}
                      </h1>
                      <Controller
                        name={name}
                        control={control}
                        rules={{ required: locale.requiredField }}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={locale.serviceTypePlaceholder}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>
                                  {locale.serviceTypePlaceholder}
                                </SelectLabel>
                                {selectFields.map(({ value, placeholder }) => (
                                  <SelectItem key={value} value={value}>
                                    {placeholder}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.serviceType && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.serviceType.message}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div key={name}>
                      <h1 className="mt-5 text-lg font-medium mb-2">
                        {getFormattedTitle(name, label)}
                      </h1>
                      <Controller
                        name={name}
                        control={control}
                        rules={getFieldValidationRules(name, locale)}
                        render={({ field }) =>
                          inputType === InputType.TEXTAREA ? (
                            <Textarea
                              {...field}
                              id={name}
                              placeholder={placeholder}
                            />
                          ) : (
                            <Input
                              {...field}
                              id={name}
                              placeholder={placeholder}
                            />
                          )
                        }
                      />
                      {errors[name] && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors[name]?.message}
                        </p>
                      )}
                    </div>
                  ),
                )}
              </div>

              <div className="flex items-center justify-center">
                <Button
                  disabled={isLoadingAddMaintenanceHistory}
                  className="w-[100px] rounded-[10px]"
                  type="submit"
                >
                  {locale.button}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Container>
  )
}

export default ServiceMaintenance
